import type { ReportTableCell, ReportTableProps } from '@/components';
import type { projectDetailUrlTypeEnum } from '@/features/mold/models';
import {
  type ReportLocalGovernmentFundTableAModel,
  type ReportLocalGovernmentFundsTableAModel,
  reportLocalGovernmentFundTableACsvHeader,
} from '@/features/mold/models/local-government-fund';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import {
  BusinessFormLabelModifier,
  OperationFormLabelModifier,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { createCsv, formatExistenceValue, formatNumberForPdf } from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';

const getReportTargetYear = (fiscalYear: number) => {
  return fiscalYear - 1;
};

/// ////////////////////////////////////////
/// CSV
/// ////////////////////////////////////////

export const getLocalGovernmentFundTableACsvHeader = (
  fiscalYear: number,
): Record<z.infer<typeof reportLocalGovernmentFundTableACsvHeader>, string> => {
  const reportYear = getReportTargetYear(fiscalYear);

  return {
    fundSheetNumber: t('report-local-government-fund-header-fund-sheet-number'),
    ministryName: t('ministry'),
    projectName: t('fund-name'),
    haveAppliedLawOfAppropriatenessOfSubsidies: t(
      'report-local-government-fund-header-have-applied-law-of-appropriateness-of-subsidies',
    ),
    numberOfLocalFundBuilding: t(
      'report-local-government-fund-header-number-of-local-fund-building-with-year',
      {
        year: reportYear,
      },
    ),
    fiscalYearOfCreation: t('fiscal-year-of-creation'),
    endDate: t('fund-scheduled-end-period-for-local-government'),
    endDateOfNewApplicationReceiving: t(
      'end-date-of-new-application-for-local-government',
    ),
    operationForms: t('operation-form'),
    businessForms: t('business-form'),
    overview: t('project-overview'),
    projectDetailPageUrl: t('project-detail-page-url'),
  };
};

export const convertLocalGovernmentFundTableAToCsv = ({
  headers,
  reports,
  projectDetailUrlType,
}: {
  headers: Record<
    z.infer<typeof reportLocalGovernmentFundTableACsvHeader>,
    string
  >;
  reports: z.infer<typeof ReportLocalGovernmentFundsTableAModel>;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}) => {
  const csvFormattedReports = reports.data.map((report) => {
    const projectDetailPageUrl = getProjectDetailPageUrl({
      isPS: projectDetailUrlType === 'public',
      projectId: report.id,
    });
    return {
      ...report,
      fundSheetNumber: `${report.fundSheetNumber}${
        report.fundSheetBranchNumber ? `-${report.fundSheetBranchNumber}` : ''
      }`,
      haveAppliedLawOfAppropriatenessOfSubsidies: formatExistenceValue(
        report.haveAppliedLawOfAppropriatenessOfSubsidies,
      ),
      operationForms:
        report.operationForms
          ?.map((d) => OperationFormLabelModifier[d])
          .join('、') ?? '',
      businessForms:
        report.businessForms
          ?.map((d) => BusinessFormLabelModifier[d])
          .join('、') ?? '',
      projectDetailPageUrl,
    };
  });

  const csvData = createCsv({
    headerKeys: reportLocalGovernmentFundTableACsvHeader.options,
    headerConverter: headers,
    body: csvFormattedReports,
  });

  return csvData;
};

/// ////////////////////////////////////////
/// PDF
/// ////////////////////////////////////////

const LOCAL_GOVERNMENT_FUND_PDF_FONT_SIZE = 5;

const rowTextBaseStyle: CSSProperties = {
  fontSize: LOCAL_GOVERNMENT_FUND_PDF_FONT_SIZE,
  textAlign: 'center',
};

const columnRatios = {
  fundSheetNumber: 0.6,
  ministryName: 1.5,
  projectName: 2,
  haveAppliedLawOfAppropriatenessOfSubsidies: 0.8,
  numberOfLocalFundBuilding: 0.8,
  fiscalYearOfCreation: 0.8,
  endDate: 1,
  endDateOfNewApplicationReceiving: 1,
  operationForms: 1,
  businessForms: 1,
  overview: 3.65,
};

export const getLocalGovernmentFundTableAHeader = (
  fiscalYear: number,
): ReportTableProps['header'] => {
  const reportYear = getReportTargetYear(fiscalYear);
  return [
    {
      type: 'text',
      value: t('report-local-government-fund-header-fund-sheet-number'),
      ratio: columnRatios.fundSheetNumber,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('ministry'),
      ratio: columnRatios.ministryName,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('fund-name'),
      ratio: columnRatios.projectName,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t(
        'report-local-government-fund-header-have-applied-law-of-appropriateness-of-subsidies',
      ),
      ratio: columnRatios.haveAppliedLawOfAppropriatenessOfSubsidies,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t(
        'report-local-government-fund-header-number-of-local-fund-building-with-year',
        {
          year: reportYear,
        },
      ),
      ratio: columnRatios.numberOfLocalFundBuilding,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('fiscal-year-of-creation'),
      ratio: columnRatios.fiscalYearOfCreation,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('fund-scheduled-end-period-for-local-government'),
      ratio: columnRatios.endDate,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('end-date-of-new-application-for-local-government'),
      ratio: columnRatios.endDateOfNewApplicationReceiving,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('operation-form'),
      ratio: columnRatios.operationForms,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('business-form'),
      ratio: columnRatios.businessForms,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('project-overview'),
      ratio: columnRatios.overview,
      textStyle: rowTextBaseStyle,
    },
  ];
};

const getLocalGovernmentFundTableARow = ({
  data,
  projectDetailUrlType,
}: {
  data: z.infer<typeof ReportLocalGovernmentFundTableAModel>;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableCell[] => {
  return [
    {
      type: 'text',
      value: `${data.fundSheetNumber.toString()}${
        data.fundSheetBranchNumber ? `-${data.fundSheetBranchNumber}` : ''
      }`,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: data.ministryName,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'link',
      href: getProjectDetailPageUrl({
        isPS: projectDetailUrlType === 'public',
        projectId: data.id,
      }),
      value: data.projectName,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: formatExistenceValue(
        data.haveAppliedLawOfAppropriatenessOfSubsidies,
      ),
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      // BEでcountしているわけではなく、form側での自由入力のためFEで0にfallbackする
      value: formatNumberForPdf(data.numberOfLocalFundBuilding) || 0,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: data.fiscalYearOfCreation ?? '',
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: data.endDate ?? '',
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: data.endDateOfNewApplicationReceiving ?? '',
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value:
        data.operationForms
          ?.map((d) => OperationFormLabelModifier[d])
          .join(', ') ?? '',
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value:
        data.businessForms
          ?.map((d) => BusinessFormLabelModifier[d])
          .join(', ') ?? '',
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: data.overview ?? '',
      textStyle: { ...rowTextBaseStyle, textAlign: 'left' },
    },
  ];
};

export const getLocalGovernmentFundTableARowData = ({
  data,
  projectDetailUrlType,
}: {
  data: z.infer<typeof ReportLocalGovernmentFundsTableAModel.shape.data>;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableProps['data'] => {
  return data.map((d) => ({
    row: getLocalGovernmentFundTableARow({ data: d, projectDetailUrlType }),
    rowStyle: {
      breakInside: 'avoid',
    },
  }));
};

export const getLocalGovernmentFundTableAFooter = (
  summaryData: z.infer<
    typeof ReportLocalGovernmentFundsTableAModel.shape.summaryData
  >,
): ReportTableProps['footer'] => [
  {
    row: [
      {
        type: 'text',
        value: t('total'),
        colSpan: 4,
        textStyle: rowTextBaseStyle,
      },
      {
        type: 'text',
        // BEでcountしているわけではなく、form側での自由入力のためFEで0にfallbackする
        value: formatNumberForPdf(summaryData.numberOfLocalFundBuilding) || 0,
        textStyle: rowTextBaseStyle,
      },
      {
        type: 'text',
        value: '',
        textStyle: rowTextBaseStyle,
      },
      {
        type: 'text',
        value: '',
        textStyle: rowTextBaseStyle,
      },
      {
        type: 'text',
        value: '',
        textStyle: rowTextBaseStyle,
      },
      {
        type: 'text',
        value: '',
        textStyle: rowTextBaseStyle,
      },
      {
        type: 'text',
        value: '',
        textStyle: rowTextBaseStyle,
      },
      {
        type: 'text',
        value: '',
        textStyle: { ...rowTextBaseStyle, textAlign: 'left' },
      },
    ],
  },
];
